import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePreviewMode } from '../hooks/usePreviewMode';
import { appendUtm } from '../lib/utm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Navigation = ({ showBackButton = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [visibility, setVisibility] = useState(null);
  const [globalContent, setGlobalContent] = useState(null);
  const { preview } = usePreviewMode();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch visibility settings
  useEffect(() => {
    const fetchVisibility = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/visibility`);
        if (response.ok) setVisibility(await response.json());
      } catch (error) {
        console.error('Failed to fetch visibility:', error);
      }
    };
    fetchVisibility();
  }, []);

  // Fetch global content for date/name
  useEffect(() => {
    fetch(`${API_URL}/api/content/global`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setGlobalContent(d); })
      .catch(() => {});
  }, []);

  const isPageVisible = (pageKey) => {
    if (preview) return true;
    if (!visibility) return true;
    // Read from the nested pages structure
    const pageSettings = visibility.pages?.[pageKey];
    if (pageSettings && pageSettings.visible === false) return false;
    return true;
  };

  const allNavLinks = [
    { name: 'Home', link: '/', section: null, pageKey: 'homepage' },
    { name: 'Program', link: '/program', section: null, pageKey: 'program' },
    { name: 'Speakers', link: '/speakers', section: null, pageKey: 'speakers' },
    { name: 'Sponsorship', link: '/sponsorship', section: null, pageKey: 'sponsorship' },
    { name: 'Golf Outing', link: '/golf-outing', section: null, pageKey: 'golf' },
    { name: 'Register', externalUrl: globalContent?.registration_url || 'https://utconferences.eventsair.com/cee/registration/Site/Register', section: null, pageKey: 'registration', isButton: true },
  ];

  const navLinks = allNavLinks.filter(link => isPageVisible(link.pageKey));
  const eventDateDisplay = globalContent?.event_dates_display || 'Coming January 2027';
  const siteName = globalContent?.event_name || 'World Nuclear Congress';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[#00457f] shadow-2xl'
            : 'bg-[#00457f]/90 backdrop-blur-md'
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Left Section */}
            <div className="flex items-center gap-3">
              {showBackButton && (
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                  data-testid="back-button"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Center Logo */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity"
            >
              <img
                src="/assets/images/wnc-logo.png"
                alt={siteName}
                className="h-10 sm:h-12 w-auto"
              />
              <div className="text-left">
                <p className="text-white font-bold text-xs sm:text-sm leading-tight">
                  {siteName}
                </p>
                <p className="text-yellow-400 text-[10px] sm:text-xs font-semibold" data-testid="nav-event-date">
                  {eventDateDisplay}
                </p>
              </div>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => {
                    if (link.externalUrl) {
                      const target = link.isButton
                        ? appendUtm(link.externalUrl, { utm_medium: 'nav' })
                        : link.externalUrl;
                      window.open(target, '_blank');
                    } else if (link.link) {
                      navigate(link.link);
                    }
                  }}
                  className={
                    link.isButton
                      ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-[#0a1a2f] px-5 py-2 rounded-full font-bold text-sm hover:from-yellow-400 hover:to-amber-400 transition-all transform hover:scale-105'
                      : `text-white/80 hover:text-white text-sm font-medium transition-colors relative group ${
                          location.pathname === link.link ? 'text-white' : ''
                        }`
                  }
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                  {!link.isButton && location.pathname === link.link && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-yellow-400 rounded-full" />
                  )}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden bg-[#00457f] shadow-2xl border-t border-white/10 transition-[max-height,opacity] duration-300 ease-out ${
            isMobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
          }`}
          data-testid="mobile-menu"
          aria-hidden={!isMobileMenuOpen}
        >
          <div className="max-w-7xl mx-auto px-6 py-4 space-y-1">
            {navLinks.filter(l => !l.isButton).map((link) => (
              <button
                key={link.name}
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  if (link.link) navigate(link.link);
                }}
                className={`w-full text-left px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center justify-between ${
                  location.pathname === link.link ? 'text-white bg-white/10 ring-1 ring-yellow-400/30' : ''
                }`}
                data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="font-medium">{link.name}</span>
                {location.pathname === link.link && (
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                )}
              </button>
            ))}
            {navLinks.some(l => l.isButton) && (
              <div className="pt-3 mt-2 border-t border-white/10">
                {navLinks.filter(l => l.isButton).map((link) => (
                  <button
                    key={link.name}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      if (link.externalUrl) window.open(appendUtm(link.externalUrl, { utm_medium: 'mobile_nav' }), '_blank');
                      else if (link.link) navigate(link.link);
                    }}
                    className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-[#0a1a2f] px-5 py-3 rounded-lg font-bold text-center hover:from-yellow-400 hover:to-amber-400 transition-all"
                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </nav>
      <div className="h-16 sm:h-20" />
    </>
  );
};

export default Navigation;
